import { EntityUser } from '@admiin-com/ds-graphql';
import { useTranslation } from 'react-i18next';
import { GridColDef, GridRowParams } from '@mui/x-data-grid';
import { WBFlex, WBBox, WBChip, WBDataGrid } from '@admiin-com/ds-web';

interface ModifiedFirmEntityUsersResultsProps {
  data: EntityUser[];
  loading: boolean;
  onNextPage: () => void;
  onRowClick: (entityUser: EntityUser) => void;
  selectedEntityUser: EntityUser | null;
  clientUser: EntityUser;
}

export function ClientsList({
  data,
  loading,
  onNextPage,
  onRowClick,
  selectedEntityUser,
  clientUser,
}: ModifiedFirmEntityUsersResultsProps) {
  const { t } = useTranslation();

  const columns: GridColDef[] = [
    {
      field: 'entityName',
      headerName: t('entityName', { ns: 'common' }),
      flex: 1,
      minWidth: 180,
      valueGetter: (_value, row) => row.entity?.name || '',
      renderCell: (params) => {
        const name = params.row.entity?.name || '';
        const hasExistingLink =
          params.row.clientGroupCode &&
          params.row.clientGroupCode === clientUser?.clientGroupCode;

        return (
          <WBFlex alignItems="center" gap={1} width="100%">
            <WBBox
              whiteSpace="nowrap"
              overflow="hidden"
              textOverflow="ellipsis"
              flex={1}
              title={name}
            >
              {name}
            </WBBox>
            {hasExistingLink && (
              <WBChip
                label={t('Linked to group', {
                  ns: 'clients',
                  code: params.row.clientCode,
                })}
                size="small"
                color="warning"
                variant="outlined"
              />
            )}
          </WBFlex>
        );
      },
    },
    {
      field: 'taxNumber',
      headerName: 'ABN',
      flex: 1,
      minWidth: 120,
      valueGetter: (_value, row) => row.entity?.taxNumber || '',
    },
    {
      field: 'companyNumber',
      headerName: 'ACN',
      flex: 1,
      minWidth: 120,
      valueGetter: (_value, row) => row.entity?.companyNumber || '',
    },
    {
      field: 'contactName',
      headerName: t('Contact name', { ns: 'common' }),
      flex: 1,
      minWidth: 150,
      valueGetter: (_value, row) =>
        row.entity?.contact?.firstName && row.entity?.contact?.lastName
          ? `${row.entity.contact.firstName} ${row.entity.contact.lastName}`
          : row.entity?.contact?.firstName || '',
    },
    {
      field: 'contactPhone',
      headerName: 'Phone',
      flex: 1,
      minWidth: 120,
      valueGetter: (_value, row) => row.entity?.contact?.phone || '',
    },
    {
      field: 'contactEmail',
      headerName: t('email', { ns: 'common' }),
      flex: 1,
      valueGetter: (_value, row) => row.entity?.contact?.email || '',
      renderCell: (params) => {
        const email = params.row.entity?.contact?.email || '';
        return (
          <WBBox
            whiteSpace="nowrap"
            overflow="hidden"
            textOverflow="ellipsis"
            width="100%"
            title={email}
          >
            {email}
          </WBBox>
        );
      },
    },
    // {
    //     field: 'clientGroupCode',
    //     headerName: 'Client group code',
    //     flex: 1,
    //     minWidth: 120,
    //     valueGetter: (_value, row) => row.clientGroupCode || '',
    // },
    // {
    //     field: 'clientCode',
    //     headerName: 'Client code',
    //     flex: 1,
    //     minWidth: 120,
    //     valueGetter: (_value, row) => row.clientCode || '',
    // },
  ];

  const handleRowClick = (params: GridRowParams) => {
    onRowClick(params.row as EntityUser);
  };

  return (
    <WBDataGrid
      rows={data ?? []}
      columns={columns}
      loading={loading}
      loadingVariant={data.length > 0 ? 'linear-progress' : 'skeleton'}
      onNextPage={onNextPage}
      rowCount={data.length}
      disableColumnMenu={false}
      disableVirtualization={false}
      disableColumnSorting={true}
      disableRowSelectionOnClick={false}
      onRowClick={handleRowClick}
      sx={{
        '& .MuiDataGrid-cell': {
          display: 'flex',
          alignItems: 'center',
        },
        '& .MuiDataGrid-row': {
          cursor: 'pointer',
          '&:hover': {
            backgroundColor: 'action.hover',
          },
        },
      }}
      isRowSelectable={(params) => true}
      rowSelectionModel={selectedEntityUser ? [selectedEntityUser.id] : []}
    />
  );
}
